import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ErrorProperties } from "../../API/identityAPIProperties";
import API from "../../API/Main/API";
import {
    ActiveAuthorizationProperties,
    UpdateAllocatedHoursProperties,
    AddAuthorizationProperties,
    AuthorizationDetailsProperties,
    GetAvailableHoursDetailProperties,
    InsuranceAuthorizationsProperties,
    GetInsuranceAuthorizationsProperties,
    AuthorizationProperties,
    CalculatedHoursRangeProperties,
    UserAllocatedHoursDetailProperties,
    GetCalculatedHoursRangeProperties,
    InsuranceAuthorizationSessionTypeProperties,
    GetAllocatedHoursDetailAuthorizationProperties,
    InsuranceAllocatedAuthorizationDetailsProperties,
    UpdateInsuranceAuthorization,
    GetUtilizationReportProperties,
    AllocationDataProperties,
    InsuranceAuthorizationTypeProperties,
} from "../../API/ClientAPIHelpers/insuranceAuthorizationProperties";
import { ConfirmationResponseProperties } from "../../API/ClientAPIHelpers/clientProperties";
import { PaginationProperties } from "../../../shared/uiComponents/Table/tableProperties";
import { AdminDashboardProperties, DashboardAuthorizationProperties } from "../../API/ClientAPIHelpers/userClientProperties";

interface InsuranceAuthorizationStateProperties {
    loading: boolean,
    allocationData: Array<AllocationDataProperties>;
    authorizationDetails: Array<ActiveAuthorizationProperties> | null,
    allocatedHoursDetails: Array<AuthorizationDetailsProperties>,
    insuranceAuthorizationSessionTypes: Array<InsuranceAuthorizationSessionTypeProperties>,
    allInsuranceAuthorizations: InsuranceAuthorizationsProperties,
    availableHoursDetails: Array<AuthorizationDetailsProperties>,
    authorizations: AdminDashboardProperties,
    insuranceAuthorizationTypes: Array<InsuranceAuthorizationTypeProperties>,
    authorizationBCBA: Array<DashboardAuthorizationProperties> | null,
    error: ErrorProperties,
}

const initialState: InsuranceAuthorizationStateProperties = {
    loading: false,
    authorizationDetails: null,
    allocationData: [],
    allocatedHoursDetails: [],
    availableHoursDetails: [],
    insuranceAuthorizationSessionTypes: [],
    allInsuranceAuthorizations: {
        query: null,
    },
    authorizations: {
        query: null,
        page: 1,
        totalNumberOfItems: 0,
        numberOfItems: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
    },
    insuranceAuthorizationTypes: [],
    authorizationBCBA: null,
    error: {
        status: 0,
        title: ""
    }
}

export const getAuthorizationInfoBCBA = createAsyncThunk(
    '/client/insurance-info/authorization-bcba',
    async (userId: string, thunkAPI) => {
        const response = await API.ClientAPI.UserClient.users.getAuthorizationInfoBCBA(userId);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response;
    }
)

export const getAuthorizationInfoClinicalSupervisor = createAsyncThunk(
    '/client/insurance-info/clinical-supervisor',
    async ({ userId, data }: { userId: string, data: PaginationProperties }, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getAuthorizationInfoClinicalSupervisor({ userId, data });
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as AdminDashboardProperties;
    }
)

export const getAuthorizationInfoAdmin = createAsyncThunk(
    '/client/insurance-info/authorization-admin',
    async (data: PaginationProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getAuthorizationInfoAdmin(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as AdminDashboardProperties;
    }
)

export const getInsuranceAuthorizationTypes = createAsyncThunk(
    '/client/insurance/types',
    async (_, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getInsuranceAuthorizationTypes();
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<InsuranceAuthorizationTypeProperties>;
    }
)

export const getUsersAllocationData = createAsyncThunk(
    '/authorization/removal/allocated-hours',
    async (data: { userIds: Array<string>, clientId: string, }, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getUsersAllocationData(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<AllocationDataProperties>;
    }
)

export const getInsuranceAuthorizationSessionTypes = createAsyncThunk(
    '/authorization/session-types',
    async (type: string, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getInsuranceAuthorizationSessionTypes(type);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response;
    }
)

export const getHourPerWeekInsuranceAuthorizationsData = createAsyncThunk(
    '/authorization/hour-per-week/data',
    async (data: GetCalculatedHoursRangeProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getInsuranceAuthorizationPerWeekData(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as CalculatedHoursRangeProperties;
    }
)

export const getActiveInsuranceAuthorizations = createAsyncThunk(
    '/authorization/active',
    async (data: { clientId: string, userId?: string }, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getActiveInsuranceAuthorizations(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<ActiveAuthorizationProperties>;
    }
)

export const getInsuranceAuthorizations = createAsyncThunk(
    '/authorization/all',
    async (data: GetInsuranceAuthorizationsProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getInsuranceAuthorizations(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as InsuranceAuthorizationsProperties;
    }
)

export const saveInsuranceAuthorizations = createAsyncThunk(
    '/authorization/save',
    async ({ clientId, data }: { clientId: string, data: AddAuthorizationProperties }, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.saveInsuranceAuthorizations(clientId, data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response;
    }
)

export const saveAssessmentInsuranceAuthorizations = createAsyncThunk(
    '/authorization/save/assessment',
    async ({ clientId, data }: { clientId: string, data: AuthorizationProperties }, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.saveAssessmentInsuranceAuthorizations(clientId, data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as ConfirmationResponseProperties;
    }
)

export const saveTreatmentInsuranceAuthorizations = createAsyncThunk(
    '/authorization/save/treatment',
    async ({ clientId, data }: { clientId: string, data: AuthorizationProperties }, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.saveTreatmentAuthorizations(clientId, data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as ConfirmationResponseProperties;
    }
)

export const getAvailableHoursDetails = createAsyncThunk(
    '/authorization/available-hours/details',
    async (data: GetAvailableHoursDetailProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getAvailableHoursDetails(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<AuthorizationDetailsProperties>;
    }
)

export const getAllocatedHoursDetailsUser = createAsyncThunk(
    '/authorization/allocated-hours/details/user',
    async (data: UserAllocatedHoursDetailProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getAllocatedHoursDetailsUser(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<AuthorizationDetailsProperties>;
    }
)


export const getAllocatedHoursAuthorizationDetails = createAsyncThunk(
    '/authorization/allocated-hours/details',
    async (data: GetAllocatedHoursDetailAuthorizationProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getAllocatedHoursAuthorizationDetails(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as Array<InsuranceAllocatedAuthorizationDetailsProperties>;
    }
)

export const updateAllocatedHours = createAsyncThunk(
    '/authorization/allocated-hours/update',
    async (data: UpdateAllocatedHoursProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.updateAllocatedHours(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response;
    }
)

export const updateAuthorization = createAsyncThunk(
    '/authorization/edit/update',
    async (data: UpdateInsuranceAuthorization, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.editAuthorization(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response;
    }
)

export const getUtilizationReport = createAsyncThunk(
    '/authorization/utilization/report',
    async (data: GetUtilizationReportProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.getUtilizationReport(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as {
            file: Blob,
            fileName: string,
        };
    }
)

export const deleteAuthorization = createAsyncThunk(
    '/authorization/delete',
    async (data: GetAvailableHoursDetailProperties, thunkAPI) => {
        const response = await API.ClientAPI.InsuranceAuthorization.deleteAuthorization(data);
        if (!!response.error) {
            return thunkAPI.rejectWithValue(response.error);
        }
        return response as { isSuccessful: boolean }
    }
)

const insuranceAuthorizationSlice = createSlice({
    name: 'insuranceAuthorization',
    initialState,
    reducers: {
        clearActiveAuthorization(state) {
            state.authorizationDetails = initialState.authorizationDetails;
        }
    },
    extraReducers: (builder) => {
        //getAuthorizationInfoBCBA
        builder.addCase(getAuthorizationInfoBCBA.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getAuthorizationInfoBCBA.fulfilled, (state, action) => {
            state.loading = false;
            state.authorizationBCBA = action.payload;
        })
        builder.addCase(getAuthorizationInfoBCBA.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getAuthorizationInfoClinicalSupervisor
        builder.addCase(getAuthorizationInfoClinicalSupervisor.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getAuthorizationInfoClinicalSupervisor.fulfilled, (state, action) => {
            state.loading = false;
            state.authorizations = action.payload;
        })
        builder.addCase(getAuthorizationInfoClinicalSupervisor.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getAuthorizationInfoAdmin
        builder.addCase(getAuthorizationInfoAdmin.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getAuthorizationInfoAdmin.fulfilled, (state, action) => {
            state.loading = false;
            state.authorizations = action.payload;
        })
        builder.addCase(getAuthorizationInfoAdmin.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getInsuranceAuthorizationTypes
        builder.addCase(getInsuranceAuthorizationTypes.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getInsuranceAuthorizationTypes.fulfilled, (state, action) => {
            state.loading = false;
            state.insuranceAuthorizationTypes = action.payload;
        })
        builder.addCase(getInsuranceAuthorizationTypes.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getUsersAllocationData
        builder.addCase(getUsersAllocationData.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getUsersAllocationData.fulfilled, (state, action) => {
            state.loading = false;
            state.allocationData = action.payload;
        })
        builder.addCase(getUsersAllocationData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //deleteAuthorization
        builder.addCase(deleteAuthorization.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(deleteAuthorization.fulfilled, (state) => {
            state.loading = false;
        })
        builder.addCase(deleteAuthorization.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getUtilizationReport
        builder.addCase(getUtilizationReport.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getUtilizationReport.fulfilled, (state) => {
            state.loading = false;
        })
        builder.addCase(getUtilizationReport.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getInsuranceAuthorizationSessionTypes
        builder.addCase(getInsuranceAuthorizationSessionTypes.pending, (state) => {
            // state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getInsuranceAuthorizationSessionTypes.fulfilled, (state, action) => {
            // state.loading = false;
            state.insuranceAuthorizationSessionTypes = action.payload;
        })
        builder.addCase(getInsuranceAuthorizationSessionTypes.rejected, (state, action) => {
            // state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getActiveInsuranceAuthorizations
        builder.addCase(getActiveInsuranceAuthorizations.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getActiveInsuranceAuthorizations.fulfilled, (state, action) => {
            state.loading = false;
            state.authorizationDetails = action.payload;
        })
        builder.addCase(getActiveInsuranceAuthorizations.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getInsuranceAuthorizations
        builder.addCase(getInsuranceAuthorizations.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getInsuranceAuthorizations.fulfilled, (state, action) => {
            state.loading = false;
            state.allInsuranceAuthorizations = action.payload;
        })
        builder.addCase(getInsuranceAuthorizations.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getAllocatedHoursDetailsUser
        builder.addCase(getAllocatedHoursDetailsUser.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getAllocatedHoursDetailsUser.fulfilled, (state, action) => {
            state.loading = false;
            state.allocatedHoursDetails = action.payload;
        })
        builder.addCase(getAllocatedHoursDetailsUser.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
        //getAvailableHoursDetails
        builder.addCase(getAvailableHoursDetails.pending, (state) => {
            state.loading = true;
            state.error = initialState.error;
        })
        builder.addCase(getAvailableHoursDetails.fulfilled, (state, action) => {
            state.loading = false;
            state.availableHoursDetails = action.payload;
        })
        builder.addCase(getAvailableHoursDetails.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as ErrorProperties;
        })
    }
})

export const { clearActiveAuthorization } = insuranceAuthorizationSlice.actions;
export default insuranceAuthorizationSlice.reducer;